import { Position } from 'geojson';
import moment from 'moment';
import { atom, selector, selectorFamily } from 'recoil';
import { CAPTURE_DURATION } from '../constants/streaming';
import { CurrentState } from '../types/currentState';
import { Point } from '../types/geoModels';
import { ChannelId } from '../types/gptTypes';
import {
  CaptureDuration,
  CaptureMode,
  StreamingState,
  StreamingTimelineEvent,
  StreamingTimezone,
  StreamType,
  Timeline,
  TimelineAvailability,
  TimelineBracket,
  TimelineRecord,
} from '../types/streaming';
import { timeMinusMinutes, timePlusMinutes, timePlusSeconds } from '../utils/dates';
import { currentStateState } from './client';

export const capturingTimelineState = atom<Timeline | undefined>({
  key: 'app.live.capturing.timeline',
  default: undefined,
});

export const loadingCapturingTimelineState = atom<boolean>({
  key: 'app.live.capturing.timeline.loading',
  default: true,
});

export const timelineRecordState = selectorFamily<TimelineRecord | undefined, string>({
  key: 'app.live.capturing.timeline.eventData',
  get:
    (eventId) =>
    ({ get }) => {
      const records = get(capturingTimelineState);
      if (!records) return;
      const event = records.find((ev) => ev.uuid === eventId);
      return event;
    },
});

export const timelineSelectedRecordState = selector<TimelineRecord | undefined>({
  key: 'app.live.capturing.timeline.selected',
  get: ({ get }) => {
    const hasCurrentState = get(capturingHasCurrentState);
    const selectedRecord = get(capturingSelectedRecordState);
    const records = get(capturingTimelineState);
    if (!records || !hasCurrentState || !selectedRecord) return;

    return get(timelineRecordState(selectedRecord));
  },
});

export const timelineSelectedOrAllRecordState = selector<Timeline | undefined>({
  key: 'app.live.capturing.timeline.selectedOrAll',
  get: ({ get }) => {
    const hasCurrentState = get(capturingHasCurrentState);
    const selectedRecord = get(capturingSelectedRecordState);
    const records = get(capturingTimelineState);
    if (!records || !hasCurrentState) return;

    if (selectedRecord) {
      const event = get(timelineRecordState(selectedRecord));
      return event ? [event] : records;
    }

    return records;
  },
});

export const timelineEvidenceEventsState = selector<StreamingTimelineEvent[] | undefined>({
  key: 'app.live.capturing.timeline.evidenceEvents',
  get: ({ get }) => {
    const records = get(capturingTimelineState);
    if (!records) return;
    const events = records.flatMap((record) => record.events?.filter((event) => !!event.key)).filter((e) => !!e);
    return events.length ? events.reverse() : undefined;
  },
});

export const timelineRecordPathsState = selector({
  key: 'app.live.capturing.timeline.record.paths',
  get: ({ get }) => {
    const recordTimeline = get(timelineSelectedOrAllRecordState);
    if (!recordTimeline?.length) return;
    const eventPaths = recordTimeline.filter((e) => e.lineStringCoordinates);

    if (eventPaths.length > 0) {
      return eventPaths;
    }

    return undefined;
  },
});

export const timelineRecordPathsLngLatsState = selector<Position[] | undefined>({
  key: 'app.live.capturing.timeline.record.paths.lnglats',
  get: ({ get }) => {
    const paths = get(timelineRecordPathsState);
    if (!paths) return;

    const validPaths = paths.filter(
      (path) => !!path.lineStringCoordinates && Array.isArray(path.lineStringCoordinates),
    );

    const lngLats = validPaths.flatMap((path) => {
      if (path.lineStringCoordinates!.length === 1) {
        return [path.lineStringCoordinates![0]];
      }
      return path.lineStringCoordinates!;
    });

    return lngLats;
  },
});

export const capturingTimelineEventState = atom<StreamingTimelineEvent | undefined>({
  key: 'app.live.capturing.timeline.event',
  default: undefined,
});

export const capturingTimelineMapEventState = atom<StreamingTimelineEvent | undefined>({
  key: 'app.live.capturing.timeline.event.map',
  default: undefined,
});

export const capturingTimelineManualDateState = atom<number | undefined>({
  key: 'app.live.capturing.timeline.date.manual',
  default: undefined,
});

export const capturingTimelineDurationState = atom<CaptureDuration>({
  key: 'app.live.capturing.timeline.duration',
  default: CaptureDuration.thirtySeconds,
});

export const capturingTimelineAvailabilityState = atom<TimelineAvailability | undefined>({
  key: 'app.live.capturing.timeline.map.availability',
  default: undefined,
});

export const capturingTimelineDateBracketsState = selectorFamily<
  TimelineBracket[] | undefined,
  { channel?: ChannelId; streamType?: StreamType }
>({
  key: 'app.live.capturing.timeline.map.brackets',
  get:
    ({ channel, streamType }) =>
    ({ get }) => {
      const availability = get(capturingTimelineAvailabilityState);
      if (!availability?.channels) return;
      let records = Object.values(availability.channels);
      if (channel) {
        records = availability.channels[`channel_${channel}`] ? [availability.channels[`channel_${channel}`]] : [];
      }
      if (streamType) {
        return records.flatMap((record) => record[streamType]);
      }
      return records.flatMap((a) => Object.values(a).flat());
    },
});

export const capturingTimelineDatesState = selector<
  | {
      startTime: number;
      endTime: number;
      id: string;
    }[]
  | undefined
>({
  key: 'app.live.capturing.timeline.map.dates',
  get: ({ get }) => {
    const records = get(capturingTimelineState);
    if (!records) return;

    const data = records.map((record) => {
      return {
        startTime: record.startTime,
        endTime: record.endTime,
        id: record.uuid,
      };
    });

    return data;
  },
});

export const capturingTimelineRecordEventsState = selectorFamily<
  { time: number; coords: [number, number] }[] | undefined,
  string
>({
  key: 'app.live.capturing.timeline.map.timedPositions',
  get:
    (eventId) =>
    ({ get }) => {
      const record = get(timelineRecordState(eventId));
      if (!record) return;
      return record.events?.map((ev) => ({
        time: ev.created,
        coords: [ev.longitude, ev.latitude],
      }));
    },
});

export const capturingMiddleDateState = selector<number | undefined>({
  key: 'app.currentStates.capturing.middleDate',
  get: ({ get }) => {
    const hasCurrentState = get(capturingHasCurrentState);
    const { startDate, endDate } = get(capturingStartEndState);
    if (!hasCurrentState || !startDate || !endDate) return;

    const middleDate = Math.floor((startDate + endDate) / 2);
    return middleDate;
  },
});

export const timelineIntermediatePositionState = selector<Point | undefined>({
  key: 'app.live.capturing.timeline.map.position',
  get: ({ get }) => {
    const middleDate = get(capturingMiddleDateState);
    if (!middleDate) return;

    const paths = get(timelineRecordPathsState);
    if (!paths) return;

    const recordEvents = paths.flatMap((path) => get(capturingTimelineRecordEventsState(path.uuid))).filter((d) => !!d);
    if (!recordEvents) return;

    let entry = recordEvents.find((item) => middleDate === item.time);
    if (!entry) {
      const candidates = recordEvents
        .map((item) => {
          const dist = Math.abs(middleDate - item.time);
          return dist <= 5 * 60 * 1000 ? { item, distance: dist } : null;
        })
        .filter(Boolean) as { item: (typeof recordEvents)[number]; distance: number }[];

      // Choose the one with the smallest distance
      if (candidates.length > 0) {
        entry = candidates.reduce((closest, curr) => (curr.distance < closest.distance ? curr : closest)).item;
      }
    }
    if (!entry) return;

    return {
      lng: entry.coords[0],
      lat: entry.coords[1],
    };
  },
});

export const capturingModeState = selector<CaptureMode>({
  key: 'app.currentStates.capturing.mode',
  get: ({ get }) => {
    return get(capturingState).captureMode;
  },
});

export const capturingHasCurrentState = selector<boolean>({
  key: 'app.currentStates.capturing.validCurrentState',
  get: ({ get }) => {
    return !!get(capturingCurrentStateState);
  },
});

export const capturingSelectedRecordState = selector<string | undefined>({
  key: 'app.currentStates.capturing.selectedRecord',
  get: ({ get }) => {
    return get(capturingState).selectedRecord;
  },
});

export const capturingStartEndState = selector<{ startDate: number | undefined; endDate: number | undefined }>({
  key: 'app.currentStates.capturing.startEnd',
  get: ({ get }) => {
    const capturingData = get(capturingState);
    return {
      startDate: capturingData.startDate,
      endDate: capturingData.endDate,
    };
  },
});

export const capturingTimelineSelectedPath = selector<number[][] | undefined>({
  key: 'app.live.capturing.timeline.map.selectedPath',
  get: ({ get }) => {
    if (!get(capturingHasCurrentState) || !(get(capturingModeState) === CaptureMode.requesting)) return;
    const dates = get(capturingTimelineDatesState);
    if (!dates) return;
    const { startDate, endDate } = get(capturingStartEndState);
    if (!startDate || !endDate) return;

    let entry = dates.find((item) => startDate >= item.startTime && endDate <= item.endTime);
    if (!entry) return;

    const recordEvents = get(capturingTimelineRecordEventsState(entry.id));
    if (!recordEvents) return;

    const filteredEventsCoords = recordEvents.filter(({ time }) => time >= startDate && time <= endDate);
    const coords = [...filteredEventsCoords];

    return coords.map(({ coords }) => coords);
  },
});

export const timelineCanRequestVideoState = selectorFamily<boolean, { channel?: ChannelId; streamType?: StreamType }>({
  key: 'app.live.capturing.timeline.canRequest',
  get:
    (metadata) =>
    ({ get }) => {
      const dateBrackets = get(capturingTimelineDateBracketsState(metadata));
      if (!dateBrackets) return false;

      const isPlaybackMode = get(capturingState).captureMode === CaptureMode.playback;

      let startDate: number | undefined = undefined;
      let endDate: number | undefined = undefined;
      if (isPlaybackMode) {
        const duration = CAPTURE_DURATION[get(playbackStreamingState).duration];
        const middleDate = get(capturingMiddleDateState);
        startDate = middleDate;
        endDate = moment(middleDate).add(duration, 'seconds').toDate().getTime();
      } else {
        const { startDate: requestingStartDate, endDate: requestingEndDate } = get(capturingStartEndState);
        startDate = requestingStartDate;
        endDate = requestingEndDate;
      }

      if (!startDate || !endDate) return false;
      let entry = dateBrackets.find((item) => startDate >= item.begin && endDate <= item.end);
      if (!entry) return false;

      return true;
    },
});

export const capturingState = atom<{
  ident?: string;
  selectedDate: number;
  selectedRecord?: string;
  captureMode: CaptureMode;
  startDate?: number;
  endDate?: number;
}>({
  key: 'app.live.capturing',
  default: {
    ident: undefined,
    selectedDate: Date.now(),
    selectedRecord: undefined,
    captureMode: CaptureMode.playback,
    startDate: undefined,
    endDate: undefined,
  },
});

export const capturingCurrentStateState = selector<CurrentState | undefined>({
  key: 'app.live.capturing.currentState',
  get: ({ get }) => {
    const ident = get(capturingState).ident;
    if (!ident) return;
    return currentStateState(ident);
  },
});

export const streamingState = atom<{
  ident?: string;
  internalState: StreamingState;
  frontState: StreamingState;
  domeState: StreamingState;
}>({
  key: 'app.live.streaming',
  default: {
    ident: undefined,
    internalState: StreamingState.ok,
    frontState: StreamingState.ok,
    domeState: StreamingState.ok,
  },
});

export const streamingCurrentStateState = selector<CurrentState | undefined>({
  key: 'app.live.streaming.currentState',
  get: ({ get }) => {
    const ident = get(streamingState).ident;
    if (!ident) return;
    return currentStateState(ident);
  },
});

export const playbackStreamingState = atom<{
  ident?: string;
  startDate?: number;
  internalStuck: boolean;
  frontStuck: boolean;
  duration: CaptureDuration;
}>({
  key: 'app.live.playbackStreaming',
  default: {
    ident: undefined,
    startDate: undefined,
    internalStuck: false,
    frontStuck: false,
    duration: CaptureDuration.thirtySeconds,
  },
});

export const playbackStreamingCurrentStateState = selector<CurrentState | undefined>({
  key: 'app.live.playbackStreaming.currentState',
  get: ({ get }) => {
    const ident = get(playbackStreamingState).ident;
    if (!ident) return;
    return currentStateState(ident);
  },
});

export const showPlaybackStreamingState = selector<boolean>({
  key: 'app.live.playbackStreaming.isVisible',
  get: ({ get }) => {
    const isCapturing = !!get(capturingState).ident;
    const playback = get(playbackStreamingState);
    const playbackCurrentState = get(playbackStreamingCurrentStateState);
    const hasValidPlayback = !!playbackCurrentState && !!playback.startDate;
    return isCapturing && hasValidPlayback;
  },
});

export const playbackStreamingMarkerTimezone = selector<StreamingTimezone>({
  key: 'app.live.playbackStreaming.marker.timezone',
  get: ({ get }) => {
    const requestedTime = get(playbackStreamingState).startDate;
    if (!requestedTime) return StreamingTimezone.outOfZone;

    const middleTime = get(capturingMiddleDateState);
    if (!middleTime) return StreamingTimezone.outOfZone;

    const startTime = timeMinusMinutes(requestedTime, 4);
    const endTime = timePlusSeconds(timePlusMinutes(requestedTime, 4), 10);

    if (middleTime) {
      const intermediateTime = timePlusMinutes(requestedTime, 2);
      if (middleTime >= startTime && middleTime < requestedTime) {
        return StreamingTimezone.past;
      }
      if (middleTime >= requestedTime && middleTime <= intermediateTime) {
        return StreamingTimezone.current;
      }
      if (middleTime > intermediateTime && middleTime <= endTime) {
        return StreamingTimezone.future;
      }
    }
    return StreamingTimezone.outOfZone;
  },
});
